import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import { Router, watchBusy } from "@frui.ts/screens";
import { Modules } from "config";
import type ChargeResponse from "entities/chargeResponse";
import type ChargeOrderSearchResponse from "entities/chargeOrderSearchResponse";
import type Payment from "entities/payment";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import PaymentsPairingRepository from "repositories/paymentsPairingRepository";
import EnumService from "services/enumService";
import LocalizationService from "services/localizationService";
import AppRouter from "services/router";
import ChargeOrderStatus from "entities/chargeOrderStatus";
import SourceType from "entities/sourceType";
import DetailViewModelWithChildren from "../../../detailViewModelWithChildren";
import PairingByOrderListViewModel from "./pairingByOrderListViewModel";
import PairingByOrderPaymentsListViewModel from "./pairingByOrderPaymentsListViewModel";
import PriceListService from "services/priceListService";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

@Router.registerRoute({ name: Router.Self, route: ":orderId" })
export default class PairingByOrderDetailViewModel extends DetailViewModelWithChildren<
  ChargeOrderSearchResponse,
  PairingByOrderPaymentsListViewModel
> {
  constructor(
    private itemId: string,
    private matchingByPaymentOrdersViewModelFactory: ReturnType<typeof PairingByOrderPaymentsListViewModel.Factory>,
    private repository: PaymentsPairingRepository,
    private enumService: EnumService,
    private priceListService: PriceListService,
    private localizationService: LocalizationService,
    private appRouter: AppRouter
  ) {
    super();
    this.navigationName = this.itemId;
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.children.push(this.matchingByPaymentOrdersViewModelFactory(this.item));
    this.tryActivateChild(this.children[0]);
  }

  @watchBusy
  protected async loadDetail(): Promise<ChargeOrderSearchResponse> {
    const item = await this.repository.getChargeOrderSearch(this.itemId);
    return this.enumService.proxyObjectEnums(item);
  }

  get navigationParams(): any {
    return undefined;
  }

  @bound
  close() {
    this.requestClose();
  }

  @computed
  get backLink() {
    return this.appRouter.getUrl(PairingByOrderListViewModel);
  }

  @computed
  get canBeOrderPaired() {
    return this.item && this.item.status !== ChargeOrderStatus.Paid;
  }

  @bound
  statusName(status: ChargeOrderStatus) {
    return this.localizationService.translateEnum("chargeOrderStatus", status);
  }

  @bound
  sourceTypeName(sourceType: SourceType) {
    return this.localizationService.translateEnum("sourceType", sourceType);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `paymentsPairing.${code}`, size);
  }

  translateChargeType(priceListItemId?: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListItemId);
  }

  translateAttribute(model: "chargeOrderSearch", attribute: PropertyName<ChargeOrderSearchResponse>): string;
  translateAttribute(model: "charge", attribute: PropertyName<ChargeResponse>): string;
  translateAttribute(entityName: "payment", attribute: PropertyName<Payment>): string;
  translateAttribute(model: "chargeOrderSearch" | "charge" | "payment", attribute: any) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  static Factory({ container }: interfaces.Context) {
    return (paymentId: string) =>
      new PairingByOrderDetailViewModel(
        paymentId,
        container.get(PairingByOrderPaymentsListViewModel.Factory),
        container.get(PaymentsPairingRepository),
        container.get(EnumService),
        container.get(PriceListService),
        container.get(LocalizationService),
        container.get(AppRouter)
      );
  }
}
